import { getCurrentOrgId, waitForOrgContext, getToken, setupOrganizationInterceptor } from '../../../../shared/orgContext';

/**
 * In production the Shell's nginx proxies /api/ to the API container.
 * In development, API_URL can be overridden via webpack DefinePlugin.
 */
const API_BASE = "/api";

/**
 * Global fetch wrapper that includes X-Org-ID header
 * This is updated when organization context changes
 */
let orgFetch: typeof fetch = fetch;

/**
 * Update the organization interceptor with new orgId
 * Called when organization context changes
 */
export function updateOrganizationInterceptor(orgId: string) {
  console.log('🔧 API: Updating organization interceptor with orgId:', orgId);
  orgFetch = setupOrganizationInterceptor(orgId);
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  let orgId = getCurrentOrgId();
  
  // Wait for organization context if not ready
  if (!orgId && !path.includes('/organization/ensure-default')) {
    orgId = await waitForOrgContext(5000);
    // Update interceptor with the orgId we just got
    if (orgId) {
      updateOrganizationInterceptor(orgId);
    }
  }
  
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Use orgFetch which includes X-Org-ID header via interceptor
  const res = await orgFetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const errorMessage = (body as { error?: string }).error || `API error: ${res.status}`;
    throw new Error(errorMessage);
  }

  return res.json() as Promise<T>;
}

/** Effective tester feature flags for the current user + active org. */
export async function getFeatureFlags(): Promise<{ flags: Record<string, boolean> }> {
  return apiFetch("/features");
}

/**
 * Fetch a document as a Blob for the shared preview modal. Same shape as
 * the document-manager helper — mirrored here so upload-inbox stays
 * decoupled from that module (each surface owns its own api client).
 * Passes ?inline=true so the server logs this as document.view_inline
 * rather than document.download.
 */
export async function fetchDocumentBlob(documentId: string): Promise<Blob> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(
    `${API_BASE}/documents/${documentId}/download?inline=true`,
    { headers },
  );
  if (!res.ok) throw new Error(`Preview failed: ${res.status}`);
  return res.blob();
}

/**
 * Fetch the server-rendered HTML preview for docx / csv documents. See
 * the doc-manager helper of the same name — mirrored here so upload-
 * inbox stays decoupled.
 */
export async function fetchDocumentPreviewHtml(
  documentId: string,
): Promise<string> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(
    `${API_BASE}/documents/${documentId}/preview`,
    { headers },
  );
  if (!res.ok) throw new Error(`Preview render failed: ${res.status}`);
  return res.text();
}

/**
 * Download a document as a Blob + filename pair. Used by the queue's
 * preview modal so users can grab the file straight from the viewer
 * they've already opened for context.
 */
export async function downloadDocument(
  documentId: string,
): Promise<{ blob: Blob; filename: string }> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await orgFetch(`${API_BASE}/documents/${documentId}/download`, {
    headers,
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);

  const cd = res.headers.get("Content-Disposition") || "";
  const match = cd.match(/filename="?(.+?)"?$/);
  const filename = match ? decodeURIComponent(match[1]) : "document";

  const blob = await res.blob();
  return { blob, filename };
}

export async function uploadDocument(
  file: File,
  tags?: string[] | null
): Promise<{ id: string; original_name: string; status: string }> {
  const token = getToken();
  let orgId = getCurrentOrgId();

  if (!orgId) {
    orgId = await waitForOrgContext(5000);
    if (orgId) {
      updateOrganizationInterceptor(orgId);
    }
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("lastModified", String(file.lastModified));
  if (tags && tags.length > 0) {
    formData.append("tags", JSON.stringify(tags));
  }

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await orgFetch(`${API_BASE}/documents/upload`, {
    method: "POST",
    headers,
    body: formData,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `Upload failed: ${res.status}`);
  }

  return res.json();
}

export async function listDocuments() {
  return apiFetch<{ documents: import("./types/api").Document[] }>(
    "/documents"
  );
}

export async function uploadGoogleDriveFile(
  fileId: string,
  accessToken: string,
  tags?: string[]
): Promise<{ id: string; original_name: string; status: string }> {
  return apiFetch("/documents/upload-google-drive", {
    method: "POST",
    body: JSON.stringify({
      fileId,
      accessToken,
      tags: tags || [],
    }),
  });
}

export async function listAuditLogs(resourceId?: string) {
  const params = resourceId ? `?resource_id=${resourceId}` : "";
  return apiFetch<{ logs: import("./types/api").AuditEntry[] }>(
    `/audit${params}`
  );
}

export async function fetchAuditPage(queryString: string) {
  return apiFetch<import("../../../../shared/audit/types").AuditPage>(
    `/audit${queryString}`
  );
}

export async function fetchAuditFacets() {
  return apiFetch<import("../../../../shared/audit/types").AuditFacets>(
    `/audit/facets`
  );
}

/**
 * Fire-and-forget page-view audit. Never throws — a failed audit write
 * must not surface to the user or block navigation. `keepalive` lets the
 * request survive if the tab is closing.
 */
export function logPageView(path: string, title?: string): void {
  apiFetch<void>("/audit/page-view", {
    method: "POST",
    body: JSON.stringify({ path, title }),
    keepalive: true,
  }).catch(() => {});
}

/**
 * Fire-and-forget session-lifecycle audit. Uses keepalive so the logout
 * write survives the redirect that immediately follows.
 */
export function logUserEvent(event: "login" | "logout"): void {
  apiFetch<void>("/audit/user-event", {
    method: "POST",
    body: JSON.stringify({ event }),
    keepalive: true,
  }).catch(() => {});
}

export async function listTeamMembers() {
  return apiFetch<{ members: import("./types/api").TeamMember[] }>("/team");
}

export async function inviteTeamMember(email: string, role: string = "MEMBER") {
  const normalizedRole = role.toUpperCase();
  const validRole =
    normalizedRole === "OWNER" || normalizedRole === "ADMIN" || normalizedRole === "MEMBER"
      ? normalizedRole
      : "MEMBER";
  const response = await apiFetch<{
    success: boolean;
    invitation: { id: string; email: string; role: string; expiresAt: string };
  }>("/invitations", {
    method: "POST",
    body: JSON.stringify({ email, role: validRole }),
  });
  return { message: `Invitation sent to ${email}.`, ...response };
}

export async function reviewDocument(
  documentId: string,
  action: "accept" | "reject",
  customName?: string
): Promise<import("./types/api").Document> {
  return apiFetch(`/documents/${documentId}/review`, {
    method: "PATCH",
    body: JSON.stringify({ action, customName }),
  });
}

export async function retryDocumentProcessing(
  documentId: string
): Promise<{ success: boolean; status: string }> {
  return apiFetch(`/documents/${documentId}/retry`, { method: "POST" });
}

// --- Tag endpoints ---

export async function listTags() {
  return apiFetch<{ tags: import("./types/api").Tag[] }>("/tags");
}

export async function addTagToDocument(documentId: string, tagId: string) {
  return apiFetch<{ success: boolean }>(`/documents/${documentId}/tags`, {
    method: "POST",
    body: JSON.stringify({ tagId }),
  });
}

export async function removeTagFromDocument(
  documentId: string,
  tagId: string
) {
  return apiFetch<{ success: boolean }>(
    `/documents/${documentId}/tags/${tagId}`,
    { method: "DELETE" }
  );
}

export async function acceptSuggestedTag(documentId: string, name: string) {
  return apiFetch<{ success: boolean; tagId: string }>(
    `/documents/${documentId}/tags/suggested/accept`,
    { method: "POST", body: JSON.stringify({ name }) }
  );
}

export async function dismissSuggestedTag(documentId: string, name: string) {
  return apiFetch<{ success: boolean }>(
    `/documents/${documentId}/tags/suggested/dismiss`,
    { method: "POST", body: JSON.stringify({ name }) }
  );
}

export async function acceptProposedTag(documentId: string, name: string) {
  return apiFetch<{ success: boolean; tagId: string }>(
    `/documents/${documentId}/tags/proposed/accept`,
    { method: "POST", body: JSON.stringify({ name }) }
  );
}

export async function dismissProposedTag(documentId: string, name: string) {
  return apiFetch<{ success: boolean }>(
    `/documents/${documentId}/tags/proposed/dismiss`,
    { method: "POST", body: JSON.stringify({ name }) }
  );
}

// --- Organization endpoints ---

export async function ensureOrganization() {
  return apiFetch<{ success: boolean; organization: { id: string; name: string } }>(
    "/organization/ensure-default",
    { method: "POST" }
  );
}

export async function getOrganization() {
  return apiFetch<{ organization: { id: string; name: string; slug: string; storage_bucket: string } }>(
    "/organization"
  );
}


export async function renameOrganization(orgId: string, name: string) {
  return apiFetch<{ organization: { id: string; name: string } }>(
    `/organization/${orgId}/settings`,
    { method: "PUT", body: JSON.stringify({ name }) }
  );
}

// --- Billing endpoints ---

export interface BillingStatus {
  plan: string | null;
  /**
   * The Stripe price purchased — its lookup key, or the price ID when it has
   * none. `plan` is the feature tier.
   */
  billingPlan: string | null;
  /** Display name of billingPlan, when that price is still on sale. */
  billingPlanLabel: string | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  hasBillingAccount: boolean;
  effectiveStatus: "active" | "trialing" | "past_due" | "canceled_active" | "incomplete" | "free";
  creditBalance: number;
  /** The org must subscribe before using the app (plan picker at sign-up). */
  subscriptionRequired: boolean;
}

/** A price on sale, from the server's Stripe-backed catalogue. */
export interface BillingPlan {
  priceId: string;
  planKey: string;
  tier: "pro" | "enterprise";
  productId: string;
  productName: string;
  label: string;
  features: string[];
  /** Minor units (cents). */
  unitAmount: number;
  currency: string;
  interval: "day" | "week" | "month" | "year";
  intervalCount: number;
}

/** Readable by any member: whether the org is locked until it subscribes. */
export async function getBillingAccess() {
  return apiFetch<{ subscriptionRequired: boolean }>("/billing/access");
}

export async function getBillingStatus() {
  return apiFetch<BillingStatus>("/billing/status");
}

export async function getBillingPlans() {
  return apiFetch<{ plans: BillingPlan[] }>("/billing/plans");
}

// The server only accepts a price that's in its catalogue, and builds the
// redirect URLs itself.
export async function createCheckoutSession(priceId: string) {
  return apiFetch<{ url: string; sessionId: string }>("/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ priceId }),
  });
}

export async function createPortalSession() {
  return apiFetch<{ url: string }>("/billing/portal", { method: "POST" });
}
